// ErrorPage — the /error SPA route (RFC-0015 Gap C).
//
// Kratos redirects self-service errors to `${PORTAL_PUBLIC_ORIGIN}/error?id=<id>`
// (kratos.yml flows.error.ui_url). Before RFC-0015 the SPA had no such route, so
// the redirect fell through to the auth-gated shell's catch-all — a 404/skeleton
// flash instead of a readable error. This route closes that gap.
//
// It fetches the Kratos error container by id and renders its message using the
// SAME card/alert presentation the /login page uses for a rejected (out-of-domain)
// flow, plus a path back to /login. It renders OUTSIDE PortalLayout / AuthProvider
// (App.tsx), so it never fires `GET /api/me`.

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchErrorFlow, type KratosErrorFlow } from '@/lib/auth-api';

type Status = 'loading' | 'ready';

/** Default copy when no id is present or the error carries no readable message. */
const FALLBACK_MESSAGE =
  'Something went wrong during sign-in. Please return to the sign-in page and try again.';

/** Extract a human-readable line from the Kratos error container, or a fallback. */
export function errorMessageText(flow: KratosErrorFlow | null): string {
  const message = flow?.error?.message?.trim();
  const reason = flow?.error?.reason?.trim();
  return message || reason || FALLBACK_MESSAGE;
}

export function ErrorPage() {
  const [searchParams] = useSearchParams();
  const errorId = searchParams.get('id');

  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState<string>(FALLBACK_MESSAGE);
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    doneRef.current = true;

    let cancelled = false;

    async function load() {
      // No id → nothing to fetch; render the graceful fallback.
      if (!errorId) {
        if (!cancelled) setStatus('ready');
        return;
      }
      try {
        const flow = await fetchErrorFlow(errorId);
        if (!cancelled) setMessage(errorMessageText(flow));
      } catch {
        // Network / non-200 — keep the graceful fallback message.
        if (!cancelled) setMessage(FALLBACK_MESSAGE);
      } finally {
        if (!cancelled) setStatus('ready');
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, [errorId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-8 shadow-(--c-shadow-md)">
        <div className="mb-6 flex flex-col gap-1.5 text-center">
          <h1 className="text-lg font-semibold text-text">Documentation Portal</h1>
          <p className="text-[13px] leading-relaxed text-muted">Sign-in error</p>
        </div>

        {status === 'loading' && (
          <div
            role="status"
            className="flex items-center justify-center gap-2 py-4 text-[13px] text-muted"
          >
            <span className="h-3.5 w-3.5 shrink-0 animate-spin-slow rounded-full border-2 border-current border-t-transparent" />
            Loading…
          </div>
        )}

        {status === 'ready' && (
          <div className="flex flex-col gap-4">
            <div
              role="alert"
              className="rounded-lg border border-[rgba(255,92,92,0.2)] bg-error-bg px-3 py-2 text-[13px] text-badge-error-text"
            >
              {message}
            </div>
            <Link
              to="/login"
              className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-4 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
            >
              Back to sign-in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
